import { useState } from 'react';
import { Badge, Card, Button, Input, LoadingSkeleton } from '@nexora/design-system';
import { formatCurrency, formatDate, formatPercent } from '../../shared/utils/format';
import { useDreGerencial, useAccountingCtx, type DreLine } from './useAccounting';

/* ═══════════════════════════════════════════════════════════════════════════
   DRE Gerencial — estrutura de contas com análise vertical
   ───────────────────────────────────────────────────────────────────────────
   Contrato consumido (backend já implementado):

   GET /accounting/dre?startDate&endDate  (AccountingReportsController)
   → DreGerencialResponseDto {
       startDate, endDate,
       budget: { available, reason },            // available=false SEMPRE: não há módulo de orçamento
       avBase: { label: 'receita_bruta'|'receita_liquida', value },
       revenue: DreLineDto[],                    // linha: code, name, level, section, realized, av,
       expenses: DreLineDto[],                   //        orcado|null, orcadoReason, orcadoStatus, var|null
       receitaBruta, deducoesDaReceita, receitaLiquida,
       totalExpenses, netIncome,
       waterfall?: WaterfallItemDto[]
     }

   Regra de ouro do módulo: o backend NÃO estima orçado — os campos `orcado`
   e `var` vêm sempre null (motivo em `budget.reason`). A tela mostra a
   coluna Orçado com "—" e um aviso, em vez de inventar números.
   ═══════════════════════════════════════════════════════════════════════════ */

function todayIso(): string {
  return new Date().toISOString().split('T')[0] ?? '';
}

function firstOfYearIso(): string {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1).toISOString().split('T')[0] ?? '';
}

/* Apresentação contábil: valor negativo entre parênteses. */
function formatAccounting(value: number): string {
  if (value < 0) return `(${formatCurrency(Math.abs(value))})`;
  return formatCurrency(value);
}

/* Dedução/despesa: saldo positivo na natureza, reduz o resultado. */
function formatDeduction(value: number): string {
  return value === 0 ? formatCurrency(0) : `(${formatCurrency(Math.abs(value))})`;
}

const DreStateLoading = ({ label }: { label: string }): JSX.Element => (
  <div className="rdr-state">
    <LoadingSkeleton />
    <span>{label}</span>
  </div>
);

const DreStateError = ({ label, error }: { label: string; error: unknown }): JSX.Element => (
  <div className="rdr-state rdr-state--error">
    <p className="rdr-state__title">{label}</p>
    <p className="rdr-state__detail">{error instanceof Error ? error.message : 'Erro desconhecido'}</p>
  </div>
);

const DreStateEmpty = ({ label }: { label: string }): JSX.Element => (
  <div className="rdr-state rdr-state--empty">
    <p>{label}</p>
  </div>
);

/* ── Linha da Estrutura de Contas ──
   O servidor devolve contas analíticas com `code`; a indentação por `level`
   reproduz a hierarquia do plano (conta sintética → analítica). Nada aqui é
   agrupado no cliente: o que o backend manda é o que se vê. */

function DreLineRow({ line }: { line: DreLine }): JSX.Element {
  const isDeduction = line.section === 'REVENUE' ? line.realized < 0 : line.realized > 0;
  const showValue = (v: number): string =>
    line.section === 'REVENUE'
      ? isDeduction ? formatDeduction(-v) : formatAccounting(v)
      : formatDeduction(v);

  const hasBudget = line.orcado !== null && line.orcado !== undefined;

  return (
    <tr className="rdr-row">
      <td className="rdr-mono" style={{ paddingLeft: `${0.75 + line.level * 1.25}rem` }}>
        {line.code}
      </td>
      <td>{line.name}</td>
      <td className="rdr-num">{showValue(line.realized)}</td>
      <td className="rdr-num rdr-muted">{line.av === null ? '—' : formatPercent(line.av / 100, 2)}</td>
      <td className="rdr-num rdr-muted">{hasBudget ? formatCurrency(line.orcado!) : '—'}</td>
      <td className="rdr-num rdr-muted">{line.var === null ? '—' : formatPercent(line.var / 100, 1)}</td>
    </tr>
  );
}

function SummaryRow({
  label,
  value,
  avPct,
  emphasis,
}: {
  label: string;
  value: number;
  /** Análise vertical da linha de total (%). null → "—" (base não aplicável). */
  avPct?: number | null;
  emphasis?: boolean;
}): JSX.Element {
  return (
    <tr className={emphasis ? 'rdr-total rdr-total--result' : 'rdr-total'}>
      <td colSpan={2}>{label}</td>
      <td className="rdr-num">{formatAccounting(value)}</td>
      <td className="rdr-num rdr-muted">
        {avPct === null || avPct === undefined ? '—' : formatPercent(avPct / 100, 2)}
      </td>
      <td className="rdr-num rdr-muted">—</td>
      <td className="rdr-num rdr-muted">—</td>
    </tr>
  );
}

export function DreGerencialPage(): JSX.Element {
  const [startDate, setStartDate] = useState(firstOfYearIso);
  const [endDate, setEndDate] = useState(todayIso);

  const ctx = useAccountingCtx();
  const { data, isLoading, error, refetch } = useDreGerencial(
    ctx,
    startDate || undefined,
    endDate || undefined,
  );

  const avBaseLabel =
    data?.avBase?.label === 'receita_bruta'
      ? 'Receita Bruta'
      : data?.avBase?.label === 'receita_liquida'
        ? 'Receita Líquida'
        : null;

  return (
    <div className="rdr-page">
      <div className="mb-lg">
        <h1 className="font-display-lg text-display-lg text-on-surface m-0">DRE Gerencial</h1>
        <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
          Demonstração do Resultado do Exercício — estrutura de contas com realizado, análise
          vertical e comparativo.
        </p>
      </div>

      <Card padding="1rem" variant="outlined" className="rdr-filters">
        <div className="rdr-filters__row">
          <div className="rdr-filters__field">
            <Input label="Data início" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="rdr-filters__field">
            <Input label="Data fim" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>Atualizar</Button>
        </div>
      </Card>

      {!data?.budget?.available && data && (
        <Card padding="0.75rem 1rem" variant="outlined" className="rdr-budget-note">
          <span className="material-symbols-outlined rdr-budget-note__icon" aria-hidden="true">info</span>
          <p className="rdr-budget-note__text">
            Orçamento indisponível: {data.budget.reason}
          </p>
        </Card>
      )}

      {isLoading && <DreStateLoading label="Carregando DRE gerencial..." />}
      {error && !isLoading && (
        <DreStateError label="Erro ao carregar DRE gerencial" error={error} />
      )}

      {!isLoading && !error && data && (
        <Card variant="outlined" padding="0">
          <div className="rdr-card__head">
            <div>
              <h3 className="rdr-card__title">Estrutura de Contas</h3>
              <p className="rdr-card__period">
                Período: {formatDate(data.startDate)} a {formatDate(data.endDate)}
                {avBaseLabel ? ` · AV base: ${avBaseLabel}` : ''}
              </p>
            </div>
            <Badge variant={data.netIncome >= 0 ? 'success' : 'error'} size="sm">
              {data.netIncome >= 0 ? 'Lucro no período' : 'Prejuízo no período'}
            </Badge>
          </div>

          {data.revenue.length === 0 && data.expenses.length === 0 ? (
            <DreStateEmpty label="Nenhum lançamento em conta de resultado no período selecionado." />
          ) : (
            <div className="rdr-scroll">
              <table className="rdr-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Descrição</th>
                    <th className="rdr-right">Realizado (R$)</th>
                    <th className="rdr-right">AV (%)</th>
                    <th className="rdr-right">Orçado (R$)</th>
                    <th className="rdr-right">Var (%)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="rdr-section">
                    <td colSpan={6}>Receitas</td>
                  </tr>
                  {data.revenue.map((line) => (
                    <DreLineRow key={line.code} line={line} />
                  ))}
                  <SummaryRow
                    label="(=) Receita Bruta"
                    value={data.receitaBruta}
                    avPct={data.receitaBruta !== 0 ? 100 : null}
                  />
                  <SummaryRow
                    label="(-) Deduções da Receita"
                    value={-data.deducoesDaReceita}
                    avPct={null}
                  />
                  <SummaryRow
                    label="(=) Receita Líquida"
                    value={data.receitaLiquida}
                    avPct={null}
                  />

                  <tr className="rdr-section">
                    <td colSpan={6}>Despesas</td>
                  </tr>
                  {data.expenses.map((line) => (
                    <DreLineRow key={line.code} line={line} />
                  ))}
                  <SummaryRow
                    label="(-) Total de Custos e Despesas"
                    value={-data.totalExpenses}
                    avPct={
                      data.avBase?.value && data.avBase.value !== 0
                        ? (data.totalExpenses / data.avBase.value) * 100
                        : null
                    }
                  />
                </tbody>
                <tfoot>
                  <SummaryRow
                    label="Resultado do Período"
                    value={data.netIncome}
                    avPct={null}
                    emphasis
                  />
                </tfoot>
              </table>
            </div>
          )}
        </Card>
      )}

      <style>{`
        .rdr-page { padding: 1.5rem; }

        .rdr-filters { margin-bottom: 1rem; }
        .rdr-filters__row { display: flex; align-items: flex-end; gap: 1rem; flex-wrap: wrap; }
        .rdr-filters__field { flex: 0 0 180px; }

        .rdr-budget-note { display: flex; align-items: flex-start; gap: 0.625rem; margin-bottom: 1rem; background: #fff8e1; border-color: #f0d48a; }
        .rdr-budget-note__icon { font-size: 18px; color: #7a5c00; flex-shrink: 0; margin-top: 1px; }
        .rdr-budget-note__text { margin: 0; font-size: 0.8125rem; color: #5c4a00; }

        .rdr-card__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; padding: 1rem 1rem 0.75rem; border-bottom: 1px solid #eceef0; }
        .rdr-card__title { margin: 0; font-size: 1.25rem; font-weight: 600; color: #191c1e; }
        .rdr-card__period { margin: 0.25rem 0 0; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; }

        .rdr-scroll { overflow-x: auto; }
        .rdr-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .rdr-table th { position: sticky; top: 0; text-align: left; padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; border-bottom: 1px solid #c4c6cf; white-space: nowrap; }
        .rdr-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #eceef0; color: #191c1e; }

        .rdr-right { text-align: right; }
        .rdr-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .rdr-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; white-space: nowrap; }
        .rdr-muted { color: #43474e; }

        .rdr-row:hover td { background: #f2f4f6; }

        .rdr-section td { padding: 0.5rem 0.75rem; background: #f7f9fb; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; color: #43474e; border-bottom: 1px solid #c4c6cf; }

        .rdr-total td { background: #f7f9fb; font-weight: 600; border-top: 1px solid #c4c6cf; }
        .rdr-total--result td { background: #eceef0; font-size: 0.9375rem; border-top: 2px solid #191c1e; }

        .rdr-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.5rem; padding: 3rem 2rem; color: #43474e; font-size: 0.875rem; }
        .rdr-state--error { color: #ba1a1a; }
        .rdr-state--empty { color: #74777f; }
        .rdr-state__title { margin: 0; font-weight: 600; }
        .rdr-state__detail { margin: 0; font-size: 0.875rem; color: #74777f; }
      `}</style>
    </div>
  );
}